import useControlledState from '@quid/react-use-controlled-state';
import { useMemo } from 'react';
import { unselect, Unselect, useListNavigation, useQuerySelectorAll } from '@cld/compound-components';
import { useChildRegistry } from './childData';
import { ID_ATTRIBUTE_NAME } from './MenuItem';
import { MenuProviderProps } from './MenuProvider';

export const useMenu = (
  defaultFocusedId?: string | Unselect,
  focusedId?: string | Unselect,
  onFocusedIdChange?: (id: string | Unselect) => void,
  onSelect?: (id: string | Unselect) => void
) => {
  const [controlledFocusedId, setControlledFocusedId] = useControlledState<string | Unselect>(
    defaultFocusedId === undefined && focusedId === undefined ? unselect : defaultFocusedId,
    focusedId,
    onFocusedIdChange
  );

  const registry = useChildRegistry();

  const { ref, result } = useQuerySelectorAll(`[${ID_ATTRIBUTE_NAME}]`);
  const ids = useMemo(() => result.map((element) => String(element.getAttribute(ID_ATTRIBUTE_NAME))), [result]);

  const getChildData = (id: unknown) => {
    const data = registry.get(id);
    if (!data) {
      throw new Error(`Cannot find data for item of id ${String(id)}. Please remember to use useRegisterChildData() hook in your list item`);
    }
    return data;
  };

  const confirm = () => {
    onSelect?.(controlledFocusedId);
    if (controlledFocusedId !== unselect) {
      getChildData(controlledFocusedId).onClick?.();
    }
  };

  const cancel = () => {
    onSelect?.(unselect);
  };

  const { focusableProps } = useListNavigation(ids, controlledFocusedId, setControlledFocusedId, confirm, cancel);

  const select = (id: string) => {
    setControlledFocusedId(id);
    onSelect?.(id);
  };

  return {
    select,
    ref,
    focusedId: controlledFocusedId,
    focusableProps,
    menuProviderProps: { registry, select, focusedId: controlledFocusedId } as MenuProviderProps,
  };
};
